import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useBackTo } from "@/hooks/useBackTo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { V2Value } from "@/features/applications/api/applications.api";

export function YesNo({
  name, value, disabled, onChange,
}: {
  name: string;
  value: V2Value;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  const current = value === true ? "yes" : value === false ? "no" : "";
  return (
    <RadioGroup
      className="flex items-center gap-6"
      value={current}
      disabled={disabled}
      onValueChange={(v) => onChange(v === "yes")}
    >
      {(["yes", "no"] as const).map((opt) => (
        <Label
          key={opt}
          htmlFor={`${name}-${opt}`}
          className={`flex items-center gap-2 text-sm font-normal ${disabled ? "text-slate-400" : "text-slate-700"}`}
        >
          <RadioGroupItem id={`${name}-${opt}`} value={opt} />
          {opt === "yes" ? "Yes" : "No"}
        </Label>
      ))}
    </RadioGroup>
  );
}

export function RadioRow({
  name, value, options, onChange,
}: {
  name: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <RadioGroup
      className="flex flex-wrap items-center gap-x-6 gap-y-2"
      value={value}
      onValueChange={onChange}
    >
      {options.map((opt) => (
        <Label key={opt} htmlFor={`${name}-${opt}`} className="flex items-center gap-2 text-sm font-normal text-slate-700">
          <RadioGroupItem id={`${name}-${opt}`} value={opt} />
          {opt}
        </Label>
      ))}
    </RadioGroup>
  );
}

export const ReqBadge = () => (
  <span className="text-destructive text-sm leading-none">*</span>
);

export function SectionCard({
  title,
  children,
  contentClassName = "pt-6",
}: {
  title: string;
  children: ReactNode;
  contentClassName?: string;
}) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

export function TotalBadge({ label, value, ok, hint }: { label: string; value: number; ok: boolean; hint: string }) {
  return (
    <div
      className={`mb-5 flex items-center justify-between rounded border px-3 py-2 text-sm ${
        ok
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-amber-50 border-amber-200 text-amber-800"
      }`}
    >
      <span>{label}</span>
      <span className="font-medium tabular-nums">
        {value}% <span className="font-normal opacity-80">· {hint}</span> {ok ? "✓" : "⚠"}
      </span>
    </div>
  );
}

export function BackLink() {
  const backTo = useBackTo("/applications/v2");
  return (
    <div className="mb-4">
      <Link
        to={backTo}
        className="text-sm text-muted-foreground hover:underline"
      >
        ← Back
      </Link>
    </div>
  );
}
